import { BE_ACCESS_TOKEN_LOCALSTORAGE_KEY } from 'hooks/useCurrentUser'

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL

function getAuthToken(): string {
  const credentials = JSON.parse(
    localStorage.getItem(BE_ACCESS_TOKEN_LOCALSTORAGE_KEY) || '{}'
  ).accessToken
  if (!credentials) {
    throw new Error('No credentials found')
  }
  return credentials
}

export async function createCrossmintWallet(): Promise<{
  walletAddress: string
  alreadyExists: boolean
}> {
  const response = await fetch(`${BACKEND_URL}/api/crossmint/create-wallet`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${getAuthToken()}`
    }
  })
  if (!response.ok) {
    throw new Error(`Failed to create Crossmint wallet: ${response.status}`)
  }
  return response.json()
}

export async function getCrossmintWalletStatus(): Promise<{
  walletAddress: string | null
  provisioned: boolean
  approvalsReady: boolean
}> {
  const response = await fetch(`${BACKEND_URL}/api/crossmint/wallet-status`, {
    method: 'GET',
    headers: {
      authorization: `Bearer ${getAuthToken()}`
    }
  })
  if (!response.ok) {
    throw new Error(`Failed to get wallet status: ${response.status}`)
  }
  return response.json()
}

// Returns the authenticated user's Crossmint wallet USDC-E balance on Base.
// Returns 0 on any error so callers can display a graceful fallback.
export async function getCrossmintWalletBalance(): Promise<number> {
  try {
    const response = await fetch(
      `${BACKEND_URL}/api/crossmint/wallet-balance`,
      {
        method: 'GET',
        cache: 'no-store',
        headers: {
          authorization: `Bearer ${getAuthToken()}`
        }
      }
    )
    if (!response.ok) return 0
    const data = await response.json()
    return Number(data?.usdc ?? data.balance ?? 0)
  } catch {
    return 0
  }
}

export interface CrossmintMyPack {
  id: string
  packTemplateId: string
  packHash: string
  purchasedAt: string
  packPurchaseTx?: string
  packOpenTx?: string
  // BE combination lifecycle. Trophy Cabinet's Packs grid should hide
  // anything that's already been opened (OPENED / OPENED_ON_CHAIN) — only
  // unopened combinations are actionable in that tab.
  status?:
    | 'AVAILABLE'
    | 'PENDING'
    | 'PROCESSED'
    | 'OPENED'
    | 'OPENED_ON_CHAIN'
    | 'FAILED'
    // Wallet owns this pack NFT on-chain but the backend found no purchase/
    // combination record for it (even by pack_token_id) — no pre-assigned
    // card data exists, so it cannot be opened via the normal flow.
    | 'ON_CHAIN_ONLY'
  packTokenId?: string | null
}

export async function getCrossmintMyPacks(): Promise<{
  walletAddress: string | null
  packs: CrossmintMyPack[]
}> {
  const response = await fetch(`${BACKEND_URL}/api/crossmint/my-packs`, {
    method: 'GET',
    cache: 'no-store',
    headers: {
      authorization: `Bearer ${getAuthToken()}`
    }
  })
  if (!response.ok) {
    throw new Error(`Failed to get user's Crossmint packs: ${response.status}`)
  }
  return response.json()
}

// Shape of one moment returned by GET /api/crossmint/my-moments. Mirrors the
// NFT<Moment> interface in src/types/index.ts:189 so callers (Trophy Cabinet,
// useUsersNFTs) can drop the result directly into their existing render code
// without a transform step.
export interface CrossmintMomentNFT {
  id: string
  mintNumber: string
  maxSupply: string
  forSale: boolean
  salePrice?: string | null
  floorPrice: number
  isDuplicate: boolean
  isGenesisBall: boolean
  templateData: {
    templateId: string
    issuedSupply: string
    maxSupply: string
    immutableData: Record<string, any>
  }
}

// Fetch the authenticated user's owned moment NFTs (the cards minted when a
// pack is opened). Returns null on any error so the caller can fall back to
// Flow Cadence for legacy NFTs the same way useUsersNFTs does for packs.
export async function getCrossmintMyMoments(): Promise<{
  walletAddress: string | null
  moments: CrossmintMomentNFT[]
} | null> {
  try {
    const response = await fetch(`${BACKEND_URL}/api/crossmint/my-moments`, {
      method: 'GET',
      cache: 'no-store',
      headers: {
        authorization: `Bearer ${getAuthToken()}`
      }
    })
    if (!response.ok) {
      console.warn(
        '[getCrossmintMyMoments] non-OK response',
        response.status,
        '— Trophy Cabinet Collectibles will fall back to Flow-only'
      )
      return null
    }
    return await response.json()
  } catch (e) {
    console.warn('[getCrossmintMyMoments] error, falling back to Flow-only', e)
    return null
  }
}

// Shape of a single pack returned by GET /api/crossmint/pack/:id. Mirrors the
// fields the FE useMintedPack hook needs to render the SinglePack detail view
// for Crossmint-purchased packs (which don't exist on Flow).
export interface CrossmintPackDetail {
  id: string
  packTemplateId: string
  packHash: string
  // OPENED_ON_CHAIN is the status the BE sets when an open is observed via
  // the on-chain event stream (vs OPENED which is set when the FE-initiated
  // /api/packs/open call completes). Both mean the pack is no longer
  // openable — the FE should treat them identically.
  status:
    | 'AVAILABLE'
    | 'PENDING'
    | 'PROCESSED'
    | 'OPENED'
    | 'OPENED_ON_CHAIN'
    | 'FAILED'
  packTokenId: string | null
  packPurchaseTx: string | null
  packOpenTx: string | null
  moments: Array<{ id: string | number; serial?: number | string }>
  purchasedAt: string
}

// Fetch a single Crossmint pack by its combination _id, with ownership check
// enforced by the backend (returns 403 if the pack belongs to a different
// user). Returns null on 404 or non-2xx so callers can transparently fall
// back to fcl.query(GET_SINGLE_NFT) for legacy Flow NFTs.
export async function getCrossmintPack(
  id: string
): Promise<CrossmintPackDetail | null> {
  try {
    const response = await fetch(
      `${BACKEND_URL}/api/crossmint/pack/${encodeURIComponent(id)}`,
      {
        method: 'GET',
        headers: {
          authorization: `Bearer ${getAuthToken()}`
        }
      }
    )
    if (!response.ok) return null
    return await response.json()
  } catch (e) {
    console.warn('[getCrossmintPack] error, will fall back to Flow', e)
    return null
  }
}

export interface OpenCrossmintPackResult {
  success: boolean
  action: 'opened' | 'already_opened'
  combinationId: string
  packTokenId: string
  txHash: string
  moments: Array<{ id: string | number; serial?: number | string }>
}

// Open a Crossmint-purchased pack via the backend (admin signs the on-chain
// openPackFromAdmin tx and mints the contained card NFTs to the user's wallet).
// Used in place of the Flow fcl.send(OPEN_HASHED_PACK) path for EVM users.
export async function openCrossmintPack(
  combinationId: string
): Promise<OpenCrossmintPackResult> {
  const response = await fetch(`${BACKEND_URL}/api/packs/open`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${getAuthToken()}`
    },
    body: JSON.stringify({ combinationId })
  })
  if (!response.ok) {
    const body = await response.text().catch(() => '')
    throw new Error(
      `Failed to open Crossmint pack (${response.status}): ${body}`
    )
  }
  return response.json()
}

export interface CrossmintOrderStatus {
  orderId: string
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED'
  packCombinationId?: string
  packCombinationStatus?:
    | 'AVAILABLE'
    | 'PENDING'
    | 'PROCESSED'
    | 'OPENED'
    | 'OPENED_ON_CHAIN'
    | 'FAILED'
}

// Poll GET /api/crossmint/order-status/:orderId after a Crossmint checkout
// completes. Returns null on any error so callers can safely ignore failures.
export async function getCrossmintOrderStatus(
  orderId: string
): Promise<CrossmintOrderStatus | null> {
  try {
    const response = await fetch(
      `${BACKEND_URL}/api/crossmint/order-status/${encodeURIComponent(
        orderId
      )}`,
      {
        method: 'GET',
        headers: { authorization: `Bearer ${getAuthToken()}` }
      }
    )
    if (!response.ok) return null
    return await response.json()
  } catch {
    return null
  }
}

export interface CrossmintActivityItem {
  type: string
  tokenId?: string | number
  txHash?: string
  timestamp?: string
  blockTimestamp?: string
  from?: string
  to?: string
  amount?: string | number
  metadata?: Record<string, any>
}

// GET /api/crossmint/activity/:wallet — wallet transfer / mint history.
// Returns empty array on any error so callers can render a graceful empty state.
export async function getCrossmintActivity(
  wallet: string
): Promise<CrossmintActivityItem[]> {
  try {
    const response = await fetch(
      `${BACKEND_URL}/api/crossmint/activity/${encodeURIComponent(wallet)}`,
      {
        method: 'GET',
        headers: { authorization: `Bearer ${getAuthToken()}` }
      }
    )
    if (!response.ok) return []
    const data = await response.json()
    return Array.isArray(data) ? data : data?.activity ?? []
  } catch {
    return []
  }
}

// POST /api/crossmint/setup-marketplace-wallet — provisions the on-chain NFT
// operator approval so the marketplace contract can transfer tokens on behalf
// of the user's custodial wallet. Called automatically by useMarketplaceListing
// when a listForSaleEvm call fails with "Admin is not approved as operator".
export async function setupCrossmintMarketplaceWallet(): Promise<void> {
  const response = await fetch(
    `${BACKEND_URL}/api/crossmint/setup-marketplace-wallet`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${getAuthToken()}`
      }
    }
  )
  if (!response.ok) {
    const body = await response.text().catch(() => '')
    throw new Error(
      `Failed to setup marketplace wallet (${response.status}): ${body}`
    )
  }
}

export async function prepareCrossmintPackOrder(
  packTemplateId: number,
  recipientWallet: string,
  pricePaid: string
): Promise<{
  orderId: string
  clientSecret: string
  packHash: string
}> {
  const response = await fetch(
    `${BACKEND_URL}/api/crossmint/prepare-pack-order`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify({ packTemplateId, recipientWallet, pricePaid })
    }
  )
  if (!response.ok) {
    // The backend returns a useful body on every failure path here — 409 "no
    // packs available", 503 "inventory not seeded", and most importantly 502
    // { error, detail } where `detail` is Crossmint's own rejection message.
    // Throwing only the status code discarded all of it and left a bare
    // "Failed to prepare pack order: 502" in the console with no way to tell
    // a Crossmint validation error from an outage.
    const body = await response.text().catch(() => '')
    let parsed: any = {}
    try {
      parsed = JSON.parse(body)
    } catch {
      // non-JSON body (proxy/gateway error page) — fall through to raw text
    }
    const detail = parsed?.detail
      ? typeof parsed.detail === 'string'
        ? parsed.detail
        : JSON.stringify(parsed.detail)
      : ''
    console.error('[Crossmint] prepare-pack-order failed', {
      status: response.status,
      error: parsed?.error,
      detail: parsed?.detail ?? body.slice(0, 500)
    })
    throw new Error(
      [
        parsed?.error || `Failed to prepare pack order: ${response.status}`,
        detail
      ]
        .filter(Boolean)
        .join(' — ')
    )
  }
  return response.json()
}

export async function setupPackWallet(): Promise<void> {
  const response = await fetch(
    `${BACKEND_URL}/api/crossmint/setup-pack-wallet`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${getAuthToken()}`
      }
    }
  )
  if (!response.ok) {
    const body = await response.text().catch(() => '')
    throw new Error(`Failed to setup pack wallet (${response.status}): ${body}`)
  }
}

// Pack purchases go straight to AFLPack.buyPackDirect using the buyer's own
// USDC. There is no Crossmint order, no checkout iframe and no order polling —
// the response arrives once the transaction is confirmed on Base.
export interface PackPurchaseInfo {
  packTemplateId: number
  wallet: string
  priceUsdc: string
  balanceUsdc: string
  shortfallUsdc: string
  canAfford: boolean
  soldOut: boolean
  issuedSupply: number
  maxSupply: number
}

export interface BuyPackResult {
  txHash: string
  tokenId: string | null
  packHash: string
  combinationId: string
  pricePaidUsdc: string
}

/** Price and balance from the same source the purchase uses, so the button
 *  cannot say "Buy" and then revert on funds. */
export async function getPackPurchaseInfo(
  packTemplateId: number
): Promise<PackPurchaseInfo> {
  const response = await fetch(
    `${BACKEND_URL}/api/packs/${packTemplateId}/purchase-info`,
    { headers: { authorization: `Bearer ${getAuthToken()}` } }
  )
  if (!response.ok) {
    const body = await response.text().catch(() => '')
    throw new Error(`Could not load pack price (${response.status}): ${body}`)
  }
  return response.json()
}

export class InsufficientFundsError extends Error {
  constructor(
    public priceUsdc: string,
    public balanceUsdc: string,
    public shortfallUsdc: string
  ) {
    super('Insufficient USDC balance')
    this.name = 'InsufficientFundsError'
  }
}

export async function buyPackWithWallet(
  packTemplateId: number
): Promise<BuyPackResult> {
  const response = await fetch(`${BACKEND_URL}/api/packs/buy`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${getAuthToken()}`
    },
    body: JSON.stringify({ packTemplateId })
  })

  if (!response.ok) {
    const body = await response.text().catch(() => '')
    let parsed: any = {}
    try {
      parsed = JSON.parse(body)
    } catch {
      // Ignore parse errors
    }
    // 402 is the backend telling us to offer a top-up rather than an error.
    if (response.status === 402 && parsed?.code === 'INSUFFICIENT_FUNDS') {
      throw new InsufficientFundsError(
        parsed.priceUsdc,
        parsed.balanceUsdc,
        parsed.shortfallUsdc
      )
    }
    throw new Error(parsed?.error || `Purchase failed (${response.status})`)
  }
  return response.json()
}
